import React from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Select, 
  MenuItem, 
  FormControl, 
  makeStyles
} from '@material-ui/core';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import { useTranslation } from '../hooks/useTranslation';

const useStyles = makeStyles((theme) => ({
  paginationContainer: {
    backgroundColor: 'var(--ikaro-gray-light)',
    borderTop: '1px solid var(--ikaro-gray-medium)',
    padding: theme.spacing(1.5, 3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  pageSizeControl: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  pageSizeSelect: {
    minWidth: 70,
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'var(--ikaro-bg-surface)',
      borderRadius: 6,
      '& fieldset': {
        borderColor: 'var(--ikaro-gray-medium)',
      },
      '&:hover fieldset': {
        borderColor: 'var(--ikaro-orange-primary)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'var(--ikaro-orange-primary)',
      },
    },
    '& .MuiSelect-select': {
      padding: '6px 12px',
      fontSize: '0.875rem',
    },
  },
  // Botões de número de página
  pageButton: {
    minWidth: 32,
    height: 32,
    padding: theme.spacing(0.5),
    borderRadius: 6,
    border: 'none',
    backgroundColor: 'var(--ikaro-bg-surface)',
    color: 'var(--ikaro-text-primary)',
    fontWeight: 500,
    fontSize: '0.875rem',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    '&:hover': {
      backgroundColor: 'var(--ikaro-orange-light)',
      color: 'var(--ikaro-orange-primary)',
    },
    '&.active': {
      backgroundColor: 'var(--ikaro-orange-primary)',
      color: '#ffffff',
      boxShadow: '0 2px 4px rgba(216, 78, 43, 0.3)',
      '&:hover': {
        backgroundColor: 'var(--ikaro-orange-hover)',
      },
    },
    '&:disabled': {
      backgroundColor: 'transparent',
      color: 'var(--ikaro-gray-medium)',
      boxShadow: 'none',
    },
  },
  // Botões de navegação (Anterior/Próximo)
  navButton: {
    height: 32,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 6,
    border: 'none',
    backgroundColor: 'var(--ikaro-bg-surface)',
    color: 'var(--ikaro-text-primary)',
    fontWeight: 500,
    fontSize: '0.813rem',
    textTransform: 'none',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    '&:hover': {
      backgroundColor: 'var(--ikaro-orange-light)',
      color: 'var(--ikaro-orange-primary)',
    },
    '&:disabled': {
      backgroundColor: 'transparent',
      color: 'var(--ikaro-gray-medium)',
      boxShadow: 'none',
    },
  },
  pageButtons: {
    display: 'flex',
    gap: theme.spacing(0.5),
    alignItems: 'center',
  },
  ellipsis: {
    color: 'var(--ikaro-gray-dark)',
    fontSize: '0.875rem',
    padding: '0 4px',
    userSelect: 'none',
  },
  infoText: {
    color: 'var(--ikaro-gray-dark)',
    fontSize: '0.875rem',
  },
}));

const JobPagination = ({ 
  page, 
  pageSize, 
  total, 
  totalPages, 
  onPageChange, 
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 50, 100],
  maxVisiblePages = 5,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('jobs');

  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, total);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage);
    }
  };

  const handlePageSizeChange = (event) => {
    onPageSizeChange(parseInt(event.target.value));
  };

  const renderPageButtons = () => {
    const buttons = [];
    
    // Calcular o intervalo de páginas para mostrar
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Ajustar o startPage se necessário
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Adicionar primeira página se não estiver visível
    if (startPage > 1) {
      buttons.push(
        <Button
          key={1}
          className={classes.pageButton}
          onClick={() => handlePageChange(1)}
        >
          1
        </Button>
      );
      
      if (startPage > 2) {
        buttons.push(
          <Typography key="ellipsis1" className={classes.ellipsis}>
            ...
          </Typography>
        );
      }
    }

    // Adicionar páginas visíveis
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <Button
          key={i}
          className={`${classes.pageButton} ${i === page ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Button>
      );
    }

    // Adicionar última página se não estiver visível
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <Typography key="ellipsis2" className={classes.ellipsis}>
            ...
          </Typography>
        );
      }
      
      buttons.push(
        <Button
          key={totalPages}
          className={classes.pageButton}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Button>
      );
    }

    return buttons;
  };

  if (total === 0) {
    return null;
  }

  return (
    <Box className={classes.paginationContainer}>
      <Box className={classes.leftSection}>
        {/* Controle de itens por página */}
        <Box className={classes.pageSizeControl}>
          <Typography className={classes.infoText}>
            {t('pagination.itemsPerPage')}
          </Typography>
          <FormControl size="small" className={classes.pageSizeSelect}>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              variant="outlined"
            >
              {pageSizeOptions.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        {/* Informações de exibição */}
        <Typography className={classes.infoText}>
          {t('pagination.showing', { start: startIndex, end: endIndex, total })}
        </Typography>
      </Box>

      <Box className={classes.rightSection}>
        {/* Botão anterior */}
        <Button
          className={classes.navButton}
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
          startIcon={<ChevronLeft style={{ fontSize: 18 }} />}
        >
          {t('pagination.previous')}
        </Button>

        {/* Botões de páginas */}
        <Box className={classes.pageButtons}>
          {renderPageButtons()}
        </Box>

        {/* Botão próximo */}
        <Button
          className={classes.navButton}
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages}
          endIcon={<ChevronRight style={{ fontSize: 18 }} />}
        >
          {t('pagination.next')}
        </Button>
      </Box>
    </Box>
  );
};

export default JobPagination; 